"use client";
import React from "react";

// Must be in this order so the attributes are built per SEO team's specifications.
export enum ScriptAttr {
  async = "async",
  defer = "defer",
  src = "src",
  type = "type",
}

export interface ScriptData {
  [ScriptAttr.async]?: boolean;
  custom?: { [key: string]: string };
  [ScriptAttr.defer]?: boolean;
  [ScriptAttr.src]?: string;
  [ScriptAttr.type]?: string;
  value?: string;
}

export interface ScriptTags {
  [key: string]: ScriptData;
}

export const removeProtocol = (stringToClean: string) => {
  if (!stringToClean) {
    return ``;
  }
  return stringToClean.replace(/^\/\/|^.*?:(\/\/)?/, ``).replace(/^\//, ``);
};

export const removeProtocolOrSlash = (stringToClean: string) => removeProtocol(stringToClean).replace(/^\//, ``);

/**
 * Processes dynamic script data.
 * This script data has a different structure than the default data.
 */
function getScriptTags(script: ScriptData[]): JSX.Element[] {
  const result: JSX.Element[] = [];

  // Ensure the script array is valid and iterate over it
  if (script && Array.isArray(script) && script.length > 0) {
    script.forEach((scriptData: ScriptData, index: number) => {
      // Handle SEO tags (type: "application/ld+json")
      if (scriptData?.type === "application/ld+json" && scriptData?.value) {
        result.push(
          <React.Fragment key={`headSeoScript${index}`}>
            <script
              dangerouslySetInnerHTML={{ __html: scriptData.value }}
              key={`script-${scriptData.type}${index}`}
              type={scriptData.type}
            />
          </React.Fragment>
        );
      } else if (scriptData?.type && scriptData?.value) {
        // Handle other script tags with type and value
        result.push(
          <React.Fragment key={`headScript${index}`}>
            <script
              dangerouslySetInnerHTML={{ __html: scriptData.value }}
              key={`script-${scriptData.type}${index}`}
              type={scriptData.type}
            />
          </React.Fragment>
        );
      } else if (scriptData?.src) {
        // Handle script tags with src attribute
        const props: ScriptData = {};
        for (const prop in ScriptAttr) {
          if (Object.prototype.hasOwnProperty.call(scriptData, prop)) {
            props[prop] = scriptData[prop];
          }
        }
        if (Object.keys(props).length > 0) {
          const key = `${removeProtocolOrSlash(props.src) || ``}`;
          result.push(
            <React.Fragment key={`headScript${index}`}>
              <script key={`script-${key || index}`} {...props} />
            </React.Fragment>
          );
        }
      }
    });
  }

  return result;
}

interface IDynamicScriptProps {
    script: ScriptData[];
  }

export function DynamicScript(props : IDynamicScriptProps) {
  const scriptTags = getScriptTags(props.script);

  return (
    <>
      {scriptTags}
    </>
  );
}
